import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    alpha,
    InputAdornment,
    IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { auth } from '../../store/actions/AuthAction';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import logo from '../../assets/images/logo.png';

export interface iLogin {}

const Login = (props: iLogin) => {
    const isAuthenticated = useSelector(
        (state: RootState) => state.auth.id !== null && typeof state.auth.id !== 'undefined'
    );
    const queryClient = useQueryClient();
    const [inputs, setInputs] = useState({ username: '', password: '' });
    const [submitted, setSubmitted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { username, password } = inputs;
    const dispatch = useDispatch();

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setInputs((inputs) => ({ ...inputs, [name]: value }));
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setSubmitted(true);
        if (username && password) {
            setLoading(true);
            console.log('dispara auth');
            dispatch(auth(username, password));
            queryClient.invalidateQueries();
        }
    };

    if (isAuthenticated) {
        console.log('Login autenticado');
        return <Navigate replace to="/" />;
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 40%, ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-20%',
                    width: '70%',
                    height: '200%',
                    background: `radial-gradient(circle, ${alpha('#fff', 0.04)} 0%, transparent 70%)`,
                    transform: 'rotate(-15deg)',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-10%',
                    width: '50%',
                    height: '100%',
                    background: `radial-gradient(circle, ${alpha('#fff', 0.03)} 0%, transparent 60%)`,
                },
            }}
        >
            <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3.5, sm: 5 },
                        borderRadius: 3,
                        boxShadow: '0 20px 60px -12px rgba(0,0,0,0.25)',
                        border: `1px solid ${alpha('#fff', 0.1)}`,
                        backdropFilter: 'blur(20px)',
                        backgroundColor: alpha('#fff', 0.97),
                    }}
                >
                    {/* Logo & Branding */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 4,
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="Fermi"
                            sx={{
                                height: 48,
                                objectFit: 'contain',
                                mb: 2,
                            }}
                        />
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            color="text.primary"
                            sx={{ mb: 0.5 }}
                        >
                            Bem-vindo
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Entre com suas credenciais para continuar
                        </Typography>
                    </Box>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Usuario"
                            name="username"
                            autoComplete="username"
                            value={username}
                            onChange={handleChange}
                            autoFocus
                            error={submitted && !username}
                            helperText={submitted && !username ? 'Informe o usuario' : ''}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2.5 }}
                        />

                        <TextField
                            fullWidth
                            label="Senha"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={password}
                            onChange={handleChange}
                            error={submitted && !password}
                            helperText={submitted && !password ? 'Informe a senha' : ''}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? (
                                                <VisibilityOff sx={{ fontSize: 18 }} />
                                            ) : (
                                                <Visibility sx={{ fontSize: 18 }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3.5 }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                py: 1.4,
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                borderRadius: 2,
                                position: 'relative',
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: '#fff' }} />
                            ) : (
                                'Entrar'
                            )}
                        </Button>
                    </form>

                    {/* Footer */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            textAlign: 'center',
                            mt: 3,
                            opacity: 0.6,
                        }}
                    >
                        Fermi - Sistema Educacional
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
